<template>
  <div>
    <slot name="header" :issues="issues"></slot>
    <div v-if="$online">
      <div class="mx-1" v-if="showFilters">
        <v-row class="px-4" align="center" justify="space-between">
          <v-col cols="12" md="6" class="d-flex">
            <v-text-field
              class="flex-grow-1"
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              :label="$t('t_search')"
              v-model="issuesSearch"
              @input="searchIssues"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6" class="d-flex justify-end">
            <v-btn-toggle v-model="viewMode" class="view-toggle" mandatory>
              <v-btn small depressed value="list" :color="viewMode === 'list' ? '#4b4d4ec7' : ''">
                <v-icon>mdi-format-list-bulleted</v-icon>
              </v-btn>

              <v-btn small depressed value="grid" :color="viewMode === 'grid' ? '#4b4d4ec7' : ''">
                <v-icon>mdi-view-grid</v-icon>
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
        <div class="search-tags-spacer" v-if="!hasIssueFilterTags"></div>
        <div class="px-4 pt-3 pb-2" v-if="hasIssueFilterTags">
          <div class="d-flex align-center" style="gap: 8px; overflow-x: auto;">
            <tag-chips :tags="filterTags" :isSelectable="true" v-model="issueTags" />
            <v-btn
              v-if="issueTags && issueTags.length"
              small
              text
              color="#1976d2"
              @click="issueTags = []"
              class="text-bold text-caption flex-shrink-0 ml-auto"
              style="height: 24px; min-width: auto; padding: 0 8px"
            >
              <v-icon small class="mr-1">mdi-close</v-icon>
              {{ $t("t_clear") }}
            </v-btn>
          </div>
        </div>
      </div>

      <v-list class="transparent pt-0" flat v-if="viewMode === 'list'">
        <div v-for="(item, i) in issues" :key="i">
          <v-list-item-group color="primary">
            <v-list-item
              link
              :to="{ name: 'issue', params: { id: item.id } }"
              class="rounded mx-2 pr-3 mb-1 grey lighten-3 issue-item"
              :class="{ 'darken-4': $vuetify.theme.dark }"
            >
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <v-skeleton-loader
                    v-if="!item.imageUrl"
                    type="image"
                    width="48"
                    height="48"
                    class="rounded-sm me-3"
                  />
                  <v-img
                    v-else
                    :src="item.imageUrl"
                    alt="issue thumbnail"
                    max-height="48"
                    max-width="48"
                    class="rounded-sm me-3"
                  />
                  <span class="flex-grow-1">
                    {{ item.description ? item.description : formatDateTime(item.createdOn) }}
                  </span>
                </v-list-item-title>
                <v-list-item-subtitle class="mt-2">
                  <template v-if="item.ownerId !== currentUserId">
                    <span class="mr-2">{{ $t("t_reported_by") }}</span>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          <app-user-avatar
                            class="comment-avatar"
                            :user="{
                              profileImageAssetId: item.owner?.profileImageAssetId,
                              profileImageAssetKey: item.owner?.profileImageAssetKey,
                              initials: item.owner?.initials,
                              userId: item.owner?.id,
                            }"
                            :size="28"
                          />
                        </span>
                      </template>
                      <span>{{ item.ownerFriendlyName }}</span>
                    </v-tooltip>
                  </template>

                  <template v-if="item.tags && item.tags.length">
                    <div @click.stop.prevent="">
                      <tag-chips :tags="item.tags" :isApplied="true" :displayCount="isMobile ? 3 : 10" />
                    </div>
                  </template>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="ms-2">
                <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </div>
      </v-list>
      <v-container v-if="viewMode === 'grid' && issues?.length > 0 && !loading" fluid>
        <v-row class="pa-4 ma-3"
          ><v-col
            v-for="(item, i) in issues"
            :key="i"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            :class="$vuetify.breakpoint.smAndDown ? 'justify-center' : 'justify-start'"
          >
            <router-link :to="{ name: 'issue', params: { id: item.id } }" class="text-decoration-none">
              <v-card
                class="rounded-xl hover-elevate fill-height"
                :class="$vuetify.breakpoint.smAndDown ? 'ma - 2' : 'ma - 0'"
                elevation="2"
                ripple
              >
                <div class="overflow-hidden">
                  <v-skeleton-loader v-if="!item.imageUrl" type="image" height="200" width="280" />
                  <v-img v-else :src="item.imageUrl" cover alt="issue image" class="transition-smooth" height="200">
                  </v-img>
                </div>
                <!-- Card Content -->
                <v-card-text class="py-3 px-4">
                  <!-- Description or Date -->
                  <div class="text-caption text-truncate text-body-1 mb-2">
                    <v-tooltip
                      v-if="
                        (item.description || formatDateTime(item.createdOn)).length >
                        ($vuetify.breakpoint.smAndDown ? 60 : 80)
                      "
                      top
                    >
                      <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          {{ (item.description ? item.description : formatDateTime(item.createdOn)) | truncate(50) }}
                        </span>
                      </template>
                      <span>{{ item.description ? item.description : formatDateTime(item.createdOn) }}</span>
                    </v-tooltip>

                    <span v-else>
                      {{ item.description ? item.description : formatDateTime(item.createdOn) }}
                    </span>
                  </div>
                  <template v-if="item.tags && item.tags.length">
                    <div @click.stop.prevent="">
                      <tag-chips :tags="item.tags" :isApplied="true" :displayCount="2" />
                    </div>
                  </template>

                  <!-- Owner Info -->
                  <div v-if="item.ownerId !== currentUserId" class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center text-truncate">
                      <span class="text-caption text-grey mr-1">{{ $t("t_reported_by") }}</span>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on">
                            <app-user-avatar
                              class="comment-avatar"
                              :user="{
                                profileImageAssetId: item.owner?.profileImageAssetId,
                                profileImageAssetKey: item.owner?.profileImageAssetKey,
                                initials: item.owner?.initials,
                                userId: item.owner?.id,
                              }"
                              :size="28"
                            />
                          </span>
                        </template>
                        <span>{{ item.ownerFriendlyName }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </router-link>
          </v-col>
        </v-row>
      </v-container>
      <div v-if="showLoadMore" class="d-flex justify-center my-2">
        <v-progress-circular indeterminate color="primary" size="32" />
      </div>

      <div v-show="hasMore" ref="infiniteScrollTrigger" style="height: 1px; margin-top: 20px"></div>
    </div>
    <h4 class="pa-4" v-if="(issues?.length == 0 && !loading) || !$online">
      {{ $t("t_no_results") }} {{ !$online ? " : " + $t("t_no_network_connection") : "" }}
    </h4>
  </div>
</template>
<script>
import Helpers from "@/mixins/helpers";
import debounce from "lodash/debounce";
import { DateTime } from "luxon";
import statusBadgeMixin from "@/mixins/statusBadge";
import { AssetService, IssueService, TagService } from "@/services";
import AppUserAvatar from "../AppUserAvatar.vue";
import { mapGetters } from "vuex";
import { TagChips } from "@ranes-inc/shared-lib";

export default {
  components: { AppUserAvatar, TagChips },
  name: "IssueList",
  mixins: [Helpers, statusBadgeMixin],
  props: {
    takeNum: {
      type: Number,
      default: 25,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    preview: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    issues: [],
    orderByField: "Id",
    ascending: false,
    skipNum: 0,
    issuesSearch: "",
    issuesTag: "",
    showLoadMore: false,
    appStateListenerAdded: false,
    hasMore: true,
    viewMode: "list",
    issueTags: [],
    tagsList: [],
    filterTags: [],
  }),
  computed: {
    ...mapGetters("user", ["user"]),
    currentUserId() {
      return this.user?.userId ? Number(this.user.userId) : null;
    },
    hasIssueFilterTags() {
      return this.filterTags.length > 0;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  watch: {
    issueTags() {
      this.loadIssues(true);
    },
  },
  methods: {
    async loadIssue(issueId) {
      const r = await IssueService.getIssue(issueId);
      this.issue = r.data;
      this.drawer = false;
    },

    async loadIssues(refresh = false, search = false) {
      if (!this.$online) return;

      if (refresh) {
        this.issues = [];
        this.hasMore = true;
        this.skipNum = 0;
      }

      if (this.loading || this.showLoadMore) return;

      if (this.skipNum === 0) {
        this.loading = true;
      } else {
        this.showLoadMore = true;
      }

      this.showLoadBar();

      let takeNum = this.takeNum;

      if (search) {
        this.skipNum = 0;
        takeNum = 50;
      }

      const params = {
        "paging.orderbyfield": this.orderByField,
        "paging.ascending": this.ascending,
        "paging.skipnum": this.skipNum,
        "paging.takenum": takeNum,
      };

      if (this.issuesSearch?.trim()) params.description = this.issuesSearch.trim();
      if (Array.isArray(this.issueTags) && this.issueTags.length > 0) {
        params.tag = this.issueTags.map((tag) => tag.id).join(",");
      }
      try {
        const r = await IssueService.getIssues(params);
        const issues = r.data;
        issues.forEach((issue) => {
          // Normalize legacy "n" status to "o" (Open)
          if (issue.status === "n") {
            issue.status = "o";
          }
          this.issues.push(issue);
        });

        if (search || refresh) {
          this.issues = issues;
        } else {
          const newIssues = issues.filter((issue) => !this.issues.some((iss) => iss.id === issue.id));
          this.issues.push(...newIssues);
        }

        this.skipNum = this.issues.length;
        this.hasMore = !r.meta?.endOfResults;

        for (let issue of this.issues) {
          issue = this.normalizeIssueTags(issue);
          if (issue?.issueImages?.[0]?.imageAssetId && issue?.issueImages?.[0]?.imageAssetPublicId && !issue.imageUrl) {
            this.loadIssueImage(issue);
          }
        }

        if (!this.issueTags || this.issueTags.length === 0) {
          const tagMap = {};
          this.issues.forEach((issue) => {
            if (Array.isArray(issue.tags)) {
              issue.tags.forEach((tag) => {
                if (tag?.id != null) tagMap[tag.id] = tag;
              });
            }
          });
          this.filterTags = Object.values(tagMap);
        }
      } catch (e) {
        console.error("Failed to load issues", e);
      } finally {
        this.loading = false;
        this.showLoadMore = false;
        this.hideLoadBar();
      }
    },
    normalizeIssueTags(issue) {
      if (!issue.tags) {
        issue.tags = [];
        return issue;
      }

      if (Array.isArray(issue.tags)) {
        issue.tags = issue.tags
          .map((t) => {
            if (typeof t === "object") return t;
            return this.tagsList.find((tag) => tag.id === t || tag.name === t);
          })
          .filter(Boolean);
      } else if (typeof issue.tags === "string") {
        const tagNames = issue.tags.split(",").map((x) => x.trim());
        issue.tags = tagNames.map((name) => this.tagsList.find((tag) => tag.name === name)).filter(Boolean);
      }

      return issue;
    },

    async loadIssueImage(issue) {
      try {
        const imageSrc = await this.getPhotoBase64(issue);
        issue.imageUrl = imageSrc;
        // trigger Vue reactivity
        this.$forceUpdate?.();
      } catch (e) {
        console.error("Failed to load image for issue", issue.id, e);
      }
    },

    loadMoreIssues() {
      this.skipNum = this.issues.length;
      this.loadIssues();
    },

    async getPhotoBase64(issue) {
      let params = { assetId: issue.issueImages[0].imageAssetId, token: issue.issueImages[0].imageAssetPublicId };
      const r = await AssetService.downloadAsset(params);
      if (r?.data?.downloadUrl == null) return;
      const d = await fetch(r.data.downloadUrl);
      const imageBlob = await d.blob();
      const base64 = await this.blobToBase64(imageBlob);
      const base64String = base64.split(",")[1];
      //reconstruct as jpeg:
      return `data:image/jpeg;base64,${base64String}`;
    },

    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = function () {
          const base64data = reader.result;
          resolve(base64data);
        };
        reader.onerror = function (error) {
          reject(error);
        };
      });
    },
    searchIssues: debounce(function () {
      this.loadIssues(true, true);
    }, 500),

    formatShortDate(dateString) {
      if (!dateString) return "";
      const date = DateTime.fromISO(dateString);
      if (!date.isValid) return "";

      const now = DateTime.now();
      const startOfToday = now.startOf("day");
      const dateStart = date.startOf("day");

      if (dateStart.equals(startOfToday)) {
        return this.$t("t_today");
      } else if (date.year === now.year) {
        return date.toFormat("MMM d"); // e.g., "Nov 12"
      } else {
        return date.toFormat("MMM d, yyyy"); // e.g., "Nov 12, 2024"
      }
    },
    async loadTags() {
      try {
        const response = await TagService.searchTags({
          taggableEntity: ["issue"],
          paging: {
            takeNum: 100,
          },
        });
        this.tagsList = response.data;
      } catch (error) {
        console.error("Error loading tags:", error);
      }
    },
  },
  async mounted() {
    await this.loadTags();

    this.loadIssues();

    if (this.preview) {
      this.loading = true;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && this.hasMore && !this.showLoadMore) {
          this.loadIssues();
        }
      },
      { threshold: 0.1 },
    );

    if (this.$refs.infiniteScrollTrigger) {
      this.observer.observe(this.$refs.infiniteScrollTrigger);
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>

<style lang="scss">
@import "@/scss/variables";

.v-application .issue .issue-item {
  border-left: 4px solid $color-issues !important;
}

.issue-content {
  position: relative;
  padding: 4px 0 4px 4px;
}

.issue-status-badge {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.issue-date-subtitle {
  opacity: 0.6;
  font-size: 11px !important;
  margin-top: 2px;
}
.fill-height {
  height: 100%;
}

.v-application .issue .search-tags-spacer {
  height: 16px;
}
</style>
